import { useState } from 'react';
import { MONS } from '../mock/data.ts';
import type { SpeciesId } from '../types.ts';
import { buildSpriteUrlChain } from '../util/sprite-urls.ts';

// Sprite with a multi-step URL fallback chain. Pokémon Showdown's sprite
// filename conventions are inconsistent across formes (mega-x/mega-y
// sometimes collapse hyphens, dex art uses different rules); when one
// candidate 404s we try the next. After the last fallback we render a
// neutral placeholder rather than a broken-image icon.
//
// We do NOT gate on the hand-curated `MONS` registry — it's optional metadata
// for nicer alt text. The agent can pass any species id (hundreds possible);
// the URL chain handles the lookup, and any species the CDN doesn't have
// degrades to the `?` placeholder.
export function Sprite({
  species,
  size = 40,
  className,
}: {
  species: SpeciesId;
  size?: number;
  className?: string;
}) {
  const mon = MONS[species];
  // MONS may have a slug that differs subtly from the species id (e.g.
  // urshifu-rapid-strike → urshifu-rapidstrike). If MONS is present use its
  // slug; otherwise fall back to the species id directly. Both flow into the
  // multi-variation URL chain.
  const candidates = buildSpriteUrlChain(mon?.slug ?? species);
  const [attempt, setAttempt] = useState(0);

  if (attempt >= candidates.length) {
    return (
      <div
        className={className}
        style={{
          width: size,
          height: size,
          background: 'var(--bg-3)',
          border: '1px solid var(--line-1)',
          borderRadius: 4,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--fg-4)',
          fontSize: size * 0.25,
        }}
        title={species}
      >
        ?
      </div>
    );
  }
  // Derive a reasonable alt name from the species id when MONS is absent:
  // "froslass-mega" → "Froslass Mega". Good enough for screen readers.
  const altName =
    mon?.name ??
    species
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  return (
    <img
      className={`sprite ${className ?? ''}`}
      src={candidates[attempt]}
      alt={altName}
      width={size}
      height={size}
      onError={() => setAttempt((a) => a + 1)}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    />
  );
}
